/**
 * API client — real budget endpoints + D1 bookmark pinning.
 */

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.bajetly.com";

let bookmark: string | null = null;

const USER_KEY = "bajetly.userId";

export function getUserId(): string | null {
  return localStorage.getItem(USER_KEY);
}

export interface Account {
  id: string;
  name: string;
  kind: "checking" | "savings" | "cash" | "credit_card" | "tracking";
}

export interface CategoryMonth {
  categoryId: string;
  categoryName: string;
  groupId: string | null;
  isCcPayment: boolean;
  ccAccountId: string | null;
  assigned: number;
  activity: number;
  available: number;
  priorAvailable: number;
  overspentReset: boolean;
  overspentOnCredit: boolean;
}

export interface Budget {
  month: string;
  toBeBudgeted: number;
  income: number;
  accounts: Array<{ accountId: string; balance: number }>;
  ccPayment: Record<string, number>;
  ccCanPay: Record<string, number>;
  categories: CategoryMonth[];
}

export interface Txn {
  id: string;
  date: string;
  account_id: string;
  category_id: string | null;
  amount: number;
  payee: string | null;
  memo: string | null;
  cleared: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };
  const userId = getUserId();
  if (userId) headers["x-user-id"] = userId;
  if (bookmark) headers["x-d1-bookmark"] = bookmark;

  const res = await fetch(API_BASE + path, { ...init, headers });
  const next = res.headers.get("x-d1-bookmark");
  if (next) bookmark = next;

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${init.method ?? "GET"} ${path} → ${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export async function ping(): Promise<boolean> {
  try {
    const r = await request<{ ok: boolean; db: string }>("/health");
    return r.ok && r.db === "up";
  } catch {
    return false;
  }
}

/** Get or create the local user row. */
export async function ensureUser(): Promise<string> {
  const existing = getUserId();
  if (existing) {
    try {
      await request("/api/entitlements");
      return existing;
    } catch {
      localStorage.removeItem(USER_KEY);
    }
  }
  const email = `web-${crypto.randomUUID().slice(0, 8)}@bajetly.app`;
  const r = await request<{ user: { id: string } }>("/api/users", {
    method: "POST",
    body: JSON.stringify({ email, name: "Web User" }),
  });
  localStorage.setItem(USER_KEY, r.user.id);
  return r.user.id;
}

export const fetchBudget = (month: string) =>
  request<Budget>(`/api/budget?month=${month}`);

export const fetchAccounts = () =>
  request<{ accounts: Account[] }>("/api/accounts");

export const createAccount = (b: { name: string; kind: string; balanceSen?: number }) =>
  request<{ account: Account }>("/api/accounts", {
    method: "POST",
    body: JSON.stringify(b),
  });

export const createCategory = (name: string) =>
  request<{ category: { id: string; name: string } }>("/api/categories", {
    method: "POST",
    body: JSON.stringify({ name }),
  });

export const fetchTxns = () => request<{ transactions: Txn[] }>("/api/transactions");

export const createTxn = (b: {
  date: string;
  accountId: string;
  categoryId?: string | null;
  amountSen: number;
  payee?: string;
  memo?: string;
}) => request<{ transaction: Txn }>("/api/transactions", {
  method: "POST",
  body: JSON.stringify(b),
});

export const assign = (month: string, categoryId: string, amountSen: number) =>
  request("/api/assign", { method: "POST", body: JSON.stringify({ month, categoryId, amountSen }) });
